import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import moment from 'moment'
import * as yup from 'yup'

import Input from 'components/UI/Input'
import Checkbox from 'components/UI/Checkbox'
import Button from 'components/UI/Button'
import ErrorMessageWithExclamation from 'components/UI/ErrorMessageWithExclamation'
import NotChangeableFieldLabel from '../../Shared/NotChangeableFieldLabel'
import UploadAvatar from '../../Upload/UploadAvatar'

import { setSwitchStep, updateNamePicture } from 'routing/fetchingfunction/user/user.action'
import {
  selectLoadingUpdateNamePicture,
  selectErrorUpdateNamePicture,
} from 'routing/fetchingfunction/user/user.reducer'

import { SwitchCreateAccountSchema } from '../../../routing/utils/validators'

import { STUDENT_ROLE, TUTOR_ROLE } from '../../../constants'

const SwitchCreateAccount = ({ role = STUDENT_ROLE }) => {
  const dispatch = useDispatch()

  const isLoading = useSelector(selectLoadingUpdateNamePicture)
  const createAccountError = useSelector(selectErrorUpdateNamePicture)

  const {
    register,
    watch,
    control,
    setValue,
    resetField,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: '',
      first_name: '',
      last_name: '',
    },
    resolver: yupResolver(
      yup.object({
        ...SwitchCreateAccountSchema,
        birth_date:
          role === TUTOR_ROLE ? yup.string().required('Birth date is required') : undefined,
      })
    ),
  })

  const profile_picture = watch('profile_picture')

  const [isCheckbox1Checked, setIsCheckbox1Checked] = useState(false)
  const [isCheckbox2Checked, setIsCheckbox2Checked] = useState(false)
  const [isCheckbox3Checked, setIsCheckbox3Checked] = useState(false)
  const [areAllCheckboxesChecked, setAreAllCheckboxesChecked] = useState(null)

  const isTutor = role === TUTOR_ROLE

  const handleFormSubmit = (data) => {
    if (!isCheckbox1Checked || !isCheckbox2Checked || !isCheckbox3Checked) {
      return setAreAllCheckboxesChecked(false)
    }

    setAreAllCheckboxesChecked(true)

    const formData = new FormData()

    formData.set('first_name', data.first_name)
    formData.set('last_name', data.last_name)
    formData.set('username', data.username)

    if (data.birth_date) {
      formData.set('birth_date', data.birth_date)
    }

    if (data.profile_picture?.[0]) {
      formData.set('profile_picture', data.profile_picture[0])
    }

    dispatch(
      updateNamePicture({
        role,
        data: formData,
      })
    ).then((response) => {
      if (!response.error) {
        dispatch(setSwitchStep(2))
      }
    })
  }

  const handleResetAvatar = () => {
    resetField('profile_picture')
  }

  useEffect(() => {
    if (profile_picture && profile_picture[0] && !profile_picture[0].type.includes('image')) {
      setValue('profile_picture', null)
    }
  }, [profile_picture])

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="bg-[#FEEBDD] rounded-lg py-16 px-[148px] text-[#F67443] max-w-[800px] w-full mx-auto my-16"
    >
      <h3 className="text-4xl font-semibold text-center">Create an Account</h3>

      {createAccountError && (
        <div className="!mt-3 text-error-500">
          <ErrorMessageWithExclamation text={createAccountError} scroll />
        </div>
      )}

      <div className="mt-6 flex flex-col items-center justify-start w-full">
        <h4 className={`${isTutor ? 'mb-2' : 'mb-6'}`}>Upload Your Photo</h4>

        {isTutor && (
          <span className="mb-[24px] text-xs text-[#F79009] font-normal">
            You need to use your real photo to create account
          </span>
        )}

        <UploadAvatar gender="M" picture={profile_picture} onResetAvatar={handleResetAvatar}>
          <input className="sr-only" type="file" {...register('profile_picture')} />
        </UploadAvatar>

        <div className="flex flex-col items-center justify-start text-xs text-primary-75">
          {role === TUTOR_ROLE ? (
            <label className="text-sm font-medium mt-3 flex items-center cursor-pointer">
              <Checkbox />

              <div className="flex flex-col items-start justify-start ml-[4px]">
                <p className="text-sm font-normal leading-[20px] text-primary-100">
                  Automatically find my face and crop the photo.
                </p>

                <p className="text-xs font-normal leading-[16px] text-[#F79009]">
                  Note: max. size can’t be more than 2 Mb (JPG, PNG, and GIF formats)
                </p>
              </div>
            </label>
          ) : (
            <>
              <span className="mb-[6px]">Max. size can’t be more than 2 MB</span>

              <span>Supports: JPG, PNG, and GIF Formats</span>
            </>
          )}

          {role === TUTOR_ROLE && (
            <span className="text-primary-75 text-[16px] mt-[24px]">
              You need to use your real name and birthday to pass KYC
            </span>
          )}
        </div>

        <div className="text-sm font-medium !mb-4 mt-[24px] w-full">
          <NotChangeableFieldLabel show={isTutor} htmlFor="first_name">
            First Name
          </NotChangeableFieldLabel>

          <Input
            id="first_name"
            wrapperClassName="w-full"
            className="w-full"
            type="text"
            placeholder="First Name"
            {...register('first_name')}
          />

          {errors.first_name && <ErrorMessageWithExclamation text={errors.first_name?.message} />}
        </div>

        <div className="text-sm font-medium !mb-4 w-full">
          <NotChangeableFieldLabel show={isTutor} htmlFor="last_name">
            Last Name
          </NotChangeableFieldLabel>

          <Input
            id="last_name"
            wrapperClassName="w-full"
            className="w-full"
            type="text"
            placeholder="Last Name"
            {...register('last_name')}
          />

          {errors.last_name ? (
            <ErrorMessageWithExclamation text={errors.last_name?.message} />
          ) : (
            <span className="my-1.5 text-[#f79009] text-xs w-full text-left">
              Your real name is hidden by default.
            </span>
          )}
        </div>

        {isTutor && (
          <div className="w-full flex flex-col items-start !mb-4">
            <NotChangeableFieldLabel isLabel={false} show={isTutor}>
              Date of birth
            </NotChangeableFieldLabel>

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Controller
                control={control}
                name="birth_date"
                render={({ field: { value, onChange } }) => {
                  return (
                    <DatePicker
                      minDate={moment().subtract(150, 'years')}
                      maxDate={moment().subtract(18, 'years')}
                      value={value}
                      onChange={onChange}
                      className="custom-date-picker !w-full"
                      slotProps={{
                        textField: {
                          readOnly: true,
                        },
                      }}
                    />
                  )
                }}
              />
            </LocalizationProvider>

            {errors.birth_date && <ErrorMessageWithExclamation text={errors.birth_date.message} />}
          </div>
        )}

        <div className="text-sm font-medium w-full mb-4">
          <NotChangeableFieldLabel show={isTutor} htmlFor="nickname">
            Nickname
          </NotChangeableFieldLabel>

          <Input
            id="nickname"
            wrapperClassName="w-full"
            className="w-full"
            type="text"
            placeholder="Nickname"
            {...register('username')}
          />

          {errors.username && <ErrorMessageWithExclamation text={errors.username?.message} />}
        </div>

        {/*<div className="w-full !mb-5">*/}
        {/*  <div className="w-full grid grid-cols-2 gap-x-4">*/}
        {/*    {displayNameOptions.map((displayNameOption) => {*/}
        {/*      return (*/}
        {/*        <Radio*/}
        {/*          key={displayNameOption.value}*/}
        {/*          wrapperClassName="border !border-primary-450 rounded-lg bg-white !py-3 !px-4 hover:cursor-pointer"*/}
        {/*          labelClassName="flex-1"*/}
        {/*          label={displayNameOption.label}*/}
        {/*          value={displayNameOption.value}*/}
        {/*          {...register('display_name')}*/}
        {/*        />*/}
        {/*      );*/}
        {/*    })}*/}
        {/*  </div>*/}
        {/*</div>*/}

        <div className="mb-6">
          <Checkbox
            label={`I’m over ${isTutor ? '18' : '16'} years old`}
            labelClassName="text-sm font-medium"
            checked={isCheckbox1Checked}
            onChange={() => setIsCheckbox1Checked(!isCheckbox1Checked)}
          />
          <Checkbox
            wrapperClassName="!items-start"
            className="!top-1"
            checked={isCheckbox2Checked}
            onChange={() => setIsCheckbox2Checked(!isCheckbox2Checked)}
            label={
              <span>
                I agree with the &nbsp;
                <Link
                  className="font-bold hover:underline hover:text-[#f67443]"
                  to="/privacy-policy"
                  target="_blank"
                >
                  Privacy Policy
                </Link>
                , &nbsp;
                <Link
                  className="font-bold hover:underline hover:text-[#f67443]"
                  to="/copyright-policy"
                  target="_blank"
                >
                  Copywriting Policy
                </Link>
                , &nbsp;
                <Link
                  className="font-bold hover:underline hover:text-[#f67443]"
                  to="/cancellation-and-refund-policy"
                  target="_blank"
                >
                  Cancellation & Refund Policy
                </Link>
                .
              </span>
            }
            labelClassName="flex-1 text-sm font-medium"
          />
          <Checkbox
            wrapperClassName="!items-start"
            className="!top-1"
            checked={isCheckbox3Checked}
            onChange={() => setIsCheckbox3Checked(!isCheckbox3Checked)}
            label={
              <span>
                I agree with the &nbsp;
                <Link
                  className="font-bold hover:underline hover:text-[#f67443]"
                  to="/disclaimer"
                  target="_blank"
                >
                  Disclaimer
                </Link>
                , &nbsp;
                <Link
                  className="font-bold hover:underline hover:text-[#f67443]"
                  to="/community-guidelines"
                  target="_blank"
                >
                  Community Guidelines
                </Link>
                , &nbsp;
                <Link
                  className="font-bold hover:underline hover:text-[#f67443]"
                  to={isTutor ? '/tutor-terms-of-use' : '/terms-of-use'}
                  target="_blank"
                >
                  {isTutor && 'Tutor '}Terms of Use
                </Link>
                ,
              </span>
            }
            labelClassName="flex-1 text-sm font-medium"
          />
          {areAllCheckboxesChecked === false && (
            <ErrorMessageWithExclamation text="Please check all the policies" />
          )}
        </div>
        <Button className="w-full font-semibold" type="submit" isLoading={isLoading}>
          Create Account
        </Button>
      </div>
    </form>
  )
}

export default SwitchCreateAccount
